// src/features/landing/SectionFieldsForm.tsx
// Type-specific field editor for a single LandingSection's content. Rendered
// inside the section editor list — one form per section_type.

import { useI18n } from '@react/hooks';
import { Input, Textarea, Button } from '@react/components';
import { Select } from '@/components/Select';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import type { Entity } from '@core/types/entity';
import type { LandingSection } from '@core/types/landing';
import { makeEntity, parseClearableFloat, parseClearableInt, type ScopeIds } from './helpers';

interface SectionFieldsFormProps {
  section: LandingSection;
  onChange: (next: LandingSection) => void;
  scope: ScopeIds;
}

function EntityRefInput({ label, entity, typeName, scope, onChange }: { label: string; entity?: Entity; typeName: string; scope: ScopeIds; onChange: (e: Entity | undefined) => void }) {
  return (
    <Input
      label={label}
      value={entity?.id ?? ''}
      onChange={(e) => onChange(e.target.value ? makeEntity(e.target.value, typeName, scope) : undefined)}
      placeholder="00000000-0000-0000-0000-000000000000"
    />
  );
}

export function SectionFieldsForm({ section, onChange, scope }: SectionFieldsFormProps) {
  const { t } = useI18n();
  const patch = (next: Partial<LandingSection>) => onChange({ ...section, ...next });

  switch (section.section_type) {
    case 'LANDING_SECTION_TYPE_HERO': {
      const c = section.hero ?? { heading: '' };
      const set = (p: Partial<typeof c>) => patch({ hero: { ...c, ...p } });
      return (
        <div className="space-y-3">
          <Input label={t('landing.section.heading')} value={c.heading} onChange={(e) => set({ heading: e.target.value })} />
          <Input label={t('landing.section.subheading')} value={c.subheading ?? ''} onChange={(e) => set({ subheading: e.target.value })} />
          <Input label={t('landing.section.backgroundImage')} value={c.background_image_url ?? ''} onChange={(e) => set({ background_image_url: e.target.value })} />
          <Select label={t('landing.section.layout')} value={c.layout ?? 'HERO_LAYOUT_CENTER'} onChange={(e) => set({ layout: e.target.value as typeof c.layout })}>
            <option value="HERO_LAYOUT_CENTER">{t('landing.heroLayout.center')}</option>
            <option value="HERO_LAYOUT_LEFT">{t('landing.heroLayout.left')}</option>
            <option value="HERO_LAYOUT_RIGHT">{t('landing.heroLayout.right')}</option>
            <option value="HERO_LAYOUT_FULL">{t('landing.heroLayout.full')}</option>
          </Select>
          <div className="grid grid-cols-2 gap-3">
            <Input label={t('landing.section.primaryButtonLabel')} value={c.primary_button?.label ?? ''} onChange={(e) => set({ primary_button: { label: e.target.value, url: c.primary_button?.url ?? '', open_new_tab: c.primary_button?.open_new_tab ?? false } })} />
            <Input label={t('landing.section.primaryButtonUrl')} value={c.primary_button?.url ?? ''} onChange={(e) => set({ primary_button: { label: c.primary_button?.label ?? '', url: e.target.value, open_new_tab: c.primary_button?.open_new_tab ?? false } })} />
          </div>
        </div>
      );
    }
    case 'LANDING_SECTION_TYPE_TEXT': {
      const c = section.text ?? { body: '' };
      const set = (p: Partial<typeof c>) => patch({ text: { ...c, ...p } });
      return (
        <div className="space-y-3">
          <Textarea label={t('landing.section.body')} value={c.body} onChange={(e) => set({ body: e.target.value })} rows={5} />
          <Select label={t('landing.section.alignment')} value={c.alignment ?? 'TEXT_ALIGNMENT_LEFT'} onChange={(e) => set({ alignment: e.target.value as typeof c.alignment })}>
            <option value="TEXT_ALIGNMENT_LEFT">{t('landing.align.left')}</option>
            <option value="TEXT_ALIGNMENT_CENTER">{t('landing.align.center')}</option>
            <option value="TEXT_ALIGNMENT_RIGHT">{t('landing.align.right')}</option>
          </Select>
        </div>
      );
    }
    case 'LANDING_SECTION_TYPE_IMAGE': {
      const c = section.image ?? { image_url: '' };
      const set = (p: Partial<typeof c>) => patch({ image: { ...c, ...p } });
      return (
        <div className="space-y-3">
          <Input label={t('landing.section.imageUrl')} value={c.image_url} onChange={(e) => set({ image_url: e.target.value })} />
          <Input label={t('landing.section.altText')} value={c.alt_text ?? ''} onChange={(e) => set({ alt_text: e.target.value })} />
          <Input label={t('landing.section.caption')} value={c.caption ?? ''} onChange={(e) => set({ caption: e.target.value })} />
          <Input label={t('landing.section.linkUrl')} value={c.link_url ?? ''} onChange={(e) => set({ link_url: e.target.value })} />
        </div>
      );
    }
    case 'LANDING_SECTION_TYPE_GALLERY': {
      const c = section.gallery ?? { items: [] };
      const items = c.items ?? [];
      const setItems = (next: typeof items) => patch({ gallery: { ...c, items: next } });
      return (
        <div className="space-y-3">
          <Select label={t('landing.section.layout')} value={c.layout ?? 'GALLERY_LAYOUT_GRID'} onChange={(e) => patch({ gallery: { ...c, layout: e.target.value as typeof c.layout } })}>
            <option value="GALLERY_LAYOUT_GRID">{t('landing.galleryLayout.grid')}</option>
            <option value="GALLERY_LAYOUT_CAROUSEL">{t('landing.galleryLayout.carousel')}</option>
            <option value="GALLERY_LAYOUT_MASONRY">{t('landing.galleryLayout.masonry')}</option>
          </Select>
          {items.map((it, i) => (
            <div key={i} className="flex items-end gap-2">
              <Input label={i === 0 ? t('landing.section.imageUrl') : undefined} value={it.image_url} onChange={(e) => setItems(items.map((x, idx) => (idx === i ? { ...x, image_url: e.target.value } : x)))} />
              <Button size="sm" variant="ghost" onClick={() => setItems(items.filter((_, idx) => idx !== i))}>
                {t('common.remove')}
              </Button>
            </div>
          ))}
          <Button size="sm" variant="secondary" onClick={() => setItems([...items, { image_url: '' }])}>
            {t('landing.section.addImage')}
          </Button>
        </div>
      );
    }
    case 'LANDING_SECTION_TYPE_CTA': {
      const c = section.cta ?? { primary_button: { label: '', url: '', open_new_tab: false } };
      const set = (p: Partial<typeof c>) => patch({ cta: { ...c, ...p } });
      return (
        <div className="space-y-3">
          <Input label={t('landing.section.heading')} value={c.heading ?? ''} onChange={(e) => set({ heading: e.target.value })} />
          <Input label={t('landing.section.subheading')} value={c.subheading ?? ''} onChange={(e) => set({ subheading: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Input label={t('landing.section.primaryButtonLabel')} value={c.primary_button?.label ?? ''} onChange={(e) => set({ primary_button: { ...c.primary_button, label: e.target.value, url: c.primary_button?.url ?? '', open_new_tab: c.primary_button?.open_new_tab ?? false } })} />
            <Input label={t('landing.section.primaryButtonUrl')} value={c.primary_button?.url ?? ''} onChange={(e) => set({ primary_button: { ...c.primary_button, label: c.primary_button?.label ?? '', url: e.target.value, open_new_tab: c.primary_button?.open_new_tab ?? false } })} />
          </div>
        </div>
      );
    }
    case 'LANDING_SECTION_TYPE_FEATURES': {
      const c = section.features ?? { items: [], columns: 3 };
      const items = c.items ?? [];
      const setItems = (next: typeof items) => patch({ features: { ...c, items: next } });
      return (
        <div className="space-y-3">
          <Input
            label={t('landing.section.columns')}
            value={String(c.columns || 3)}
            onChange={(e) => patch({ features: { ...c, columns: parseClearableInt(e.target.value) ?? 3 } })}
            inputMode="numeric"
          />
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-[1fr_2fr_auto] items-end gap-2 rounded-md border border-border p-2">
              <Input label={i === 0 ? t('landing.section.icon') : undefined} value={it.icon ?? ''} onChange={(e) => setItems(items.map((x, idx) => (idx === i ? { ...x, icon: e.target.value } : x)))} />
              <Input label={i === 0 ? t('landing.section.title') : undefined} value={it.title} onChange={(e) => setItems(items.map((x, idx) => (idx === i ? { ...x, title: e.target.value } : x)))} />
              <Button size="sm" variant="ghost" onClick={() => setItems(items.filter((_, idx) => idx !== i))}>
                {t('common.remove')}
              </Button>
            </div>
          ))}
          <Button size="sm" variant="secondary" onClick={() => setItems([...items, { title: '' }])}>
            {t('landing.section.addFeature')}
          </Button>
        </div>
      );
    }
    case 'LANDING_SECTION_TYPE_FORM': {
      const c = section.form ?? {};
      const set = (p: Partial<typeof c>) => patch({ form: { ...c, ...p } });
      return (
        <div className="space-y-3">
          <EntityRefInput label={t('landing.section.formId')} entity={c.form} typeName="form" scope={scope} onChange={(e) => set({ form: e })} />
          <Input label={t('landing.section.heading')} value={c.heading ?? ''} onChange={(e) => set({ heading: e.target.value })} />
          <Textarea label={t('landing.section.description')} value={c.description ?? ''} onChange={(e) => set({ description: e.target.value })} rows={2} />
        </div>
      );
    }
    case 'LANDING_SECTION_TYPE_BOOKING': {
      const c = section.booking ?? {};
      const set = (p: Partial<typeof c>) => patch({ booking: { ...c, ...p } });
      return (
        <div className="space-y-3">
          <EntityRefInput label={t('landing.section.bookableResourceId')} entity={c.bookable_resource} typeName="bookable_resource" scope={scope} onChange={(e) => set({ bookable_resource: e })} />
          <Input label={t('landing.section.heading')} value={c.heading ?? ''} onChange={(e) => set({ heading: e.target.value })} />
          <Select label={t('landing.section.widgetMode')} value={c.widget_mode ?? 'BOOKING_WIDGET_MODE_CALENDAR'} onChange={(e) => set({ widget_mode: e.target.value as typeof c.widget_mode })}>
            <option value="BOOKING_WIDGET_MODE_CALENDAR">{t('landing.bookingMode.calendar')}</option>
            <option value="BOOKING_WIDGET_MODE_LIST">{t('landing.bookingMode.list')}</option>
            <option value="BOOKING_WIDGET_MODE_COMPACT">{t('landing.bookingMode.compact')}</option>
          </Select>
        </div>
      );
    }
    case 'LANDING_SECTION_TYPE_VIDEO': {
      const c = section.video ?? { video_url: '', autoplay: false };
      const set = (p: Partial<typeof c>) => patch({ video: { ...c, ...p } });
      return (
        <div className="space-y-3">
          <Input label={t('landing.section.videoUrl')} value={c.video_url} onChange={(e) => set({ video_url: e.target.value })} />
          <Input label={t('landing.section.caption')} value={c.caption ?? ''} onChange={(e) => set({ caption: e.target.value })} />
          <ToggleSwitch label={t('landing.section.autoplay')} checked={c.autoplay} onChange={(v) => set({ autoplay: v })} />
        </div>
      );
    }
    case 'LANDING_SECTION_TYPE_FAQ': {
      const c = section.faq ?? { items: [] };
      const items = c.items ?? [];
      const setItems = (next: typeof items) => patch({ faq: { items: next } });
      return (
        <div className="space-y-3">
          {items.map((it, i) => (
            <div key={i} className="space-y-2 rounded-md border border-border p-2">
              <Input label={t('landing.section.question')} value={it.question} onChange={(e) => setItems(items.map((x, idx) => (idx === i ? { ...x, question: e.target.value } : x)))} />
              <Textarea label={t('landing.section.answer')} value={it.answer} onChange={(e) => setItems(items.map((x, idx) => (idx === i ? { ...x, answer: e.target.value } : x)))} rows={2} />
              <Button size="sm" variant="ghost" onClick={() => setItems(items.filter((_, idx) => idx !== i))}>
                {t('common.remove')}
              </Button>
            </div>
          ))}
          <Button size="sm" variant="secondary" onClick={() => setItems([...items, { question: '', answer: '' }])}>
            {t('landing.section.addFaq')}
          </Button>
        </div>
      );
    }
    case 'LANDING_SECTION_TYPE_TESTIMONIALS': {
      const c = section.testimonials ?? { items: [] };
      const items = c.items ?? [];
      const setItems = (next: typeof items) => patch({ testimonials: { ...c, items: next } });
      return (
        <div className="space-y-3">
          {items.map((it, i) => (
            <div key={i} className="space-y-2 rounded-md border border-border p-2">
              <Textarea label={t('landing.section.quote')} value={it.quote} onChange={(e) => setItems(items.map((x, idx) => (idx === i ? { ...x, quote: e.target.value } : x)))} rows={2} />
              <div className="grid grid-cols-2 gap-2">
                <Input label={t('landing.section.authorName')} value={it.author_name} onChange={(e) => setItems(items.map((x, idx) => (idx === i ? { ...x, author_name: e.target.value } : x)))} />
                <Input label={t('landing.section.authorTitle')} value={it.author_title ?? ''} onChange={(e) => setItems(items.map((x, idx) => (idx === i ? { ...x, author_title: e.target.value } : x)))} />
              </div>
              <Button size="sm" variant="ghost" onClick={() => setItems(items.filter((_, idx) => idx !== i))}>
                {t('common.remove')}
              </Button>
            </div>
          ))}
          <Button size="sm" variant="secondary" onClick={() => setItems([...items, { quote: '', author_name: '' }])}>
            {t('landing.section.addTestimonial')}
          </Button>
        </div>
      );
    }
    case 'LANDING_SECTION_TYPE_CONTACTS': {
      const c = section.contacts ?? { social_links: [] };
      const set = (p: Partial<typeof c>) => patch({ contacts: { ...c, ...p } });
      return (
        <div className="space-y-3">
          <Input label={t('landing.section.heading')} value={c.heading ?? ''} onChange={(e) => set({ heading: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Input label={t('landing.section.email')} value={c.email ?? ''} onChange={(e) => set({ email: e.target.value })} />
            <Input label={t('landing.section.phone')} value={c.phone ?? ''} onChange={(e) => set({ phone: e.target.value })} />
          </div>
          <Input label={t('landing.section.address')} value={c.address ?? ''} onChange={(e) => set({ address: e.target.value })} />
          <Input label={t('landing.section.workingHours')} value={c.working_hours ?? ''} onChange={(e) => set({ working_hours: e.target.value })} />
        </div>
      );
    }
    case 'LANDING_SECTION_TYPE_MAP': {
      const c = section.map ?? { latitude: 0, longitude: 0 };
      const set = (p: Partial<typeof c>) => patch({ map: { ...c, ...p } });
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input label={t('landing.section.latitude')} value={String(c.latitude ?? '')} onChange={(e) => set({ latitude: parseClearableFloat(e.target.value) ?? 0 })} inputMode="decimal" />
            <Input label={t('landing.section.longitude')} value={String(c.longitude ?? '')} onChange={(e) => set({ longitude: parseClearableFloat(e.target.value) ?? 0 })} inputMode="decimal" />
          </div>
          <Input label={t('landing.section.markerTitle')} value={c.marker_title ?? ''} onChange={(e) => set({ marker_title: e.target.value })} />
          <Input label={t('landing.section.address')} value={c.address ?? ''} onChange={(e) => set({ address: e.target.value })} />
        </div>
      );
    }
    case 'LANDING_SECTION_TYPE_HTML': {
      const c = section.html ?? { html: '' };
      return <Textarea label={t('landing.section.html')} value={c.html} onChange={(e) => patch({ html: { html: e.target.value } })} rows={6} />;
    }
    case 'LANDING_SECTION_TYPE_DIVIDER':
      return <p className="text-sm text-muted-foreground">{t('landing.section.dividerHelp')}</p>;
    default:
      return null;
  }
}
